import React, {useState} from 'react';
import {StorageObject} from '../../../models/StorageObject';
import {TabButton, TabNavigation, TabNavigationWrapper} from './FileMetaDataTabView.style';
import {getTab} from '../../../utils/TabUtils';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {MetaDataTab} from '../MetaDataTab';
import EventEmitter from 'events';
import {Label} from '../../../models/Label';
import {t} from 'i18next';
import {FileMetaDataTabMain} from './FileMetaDataTabMain';
import {FileMetaDataTabAdditional} from './FileMetaDataTabAdditional';
import {FileMetaDataTabPermissions} from './FileMetaDataTabPermissions';
import {FileMetaDataTabLocales} from './FileMetaDataTabLocales';
import {config} from '../../../init/config';
import {FileMetaDataTabUserLabels} from './FileMetaDataTabUserLabels';
import {LabelDTO} from '../../../models/dto/LabelDTO';
import {DisclaimerDTO} from '../../../models/dto/DisclaimerDTO';
import {FileMetaDataTabDisclaimer} from './FileMetaDataTabDisclaimer';
import {FileMetaDataTabImage} from './FileMetaDataTabImage';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_NAME,
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_COMMENT,
    STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO,
    STORAGE_OBJECT_OBJECT_METADATA_DISCLAIMER,
    STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_TAGS,
    STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL,
    STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS,
    STORAGE_OBJECT_OBJECT_METADATA_VERSION,
    STORAGE_OBJECT_TOTAL_SIZE
} from '../../../init/storage_object_constants';

export interface FileMetaDataTabViewProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly item: StorageObject;
    readonly thumbnailFile: File | null;
    readonly currentPath: string;
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    readonly disclaimerData: DisclaimerDTO[];
    readonly labelData: LabelDTO[];
    readonly isWatermarkApplicable: boolean;
    readonly labelsInitialState: Map<string, Label>;
    readonly versionInitialState: string;
    readonly eventEmitter: EventEmitter;
}

const getTabs = (): string[] => {
    let tabs: string[] = [MetaDataTab.MAIN, MetaDataTab.ADDITIONAL, MetaDataTab.PERMISSIONS, MetaDataTab.LOCALES];
    if (config.LABEL_ENABLED) {
        tabs.push(MetaDataTab.LABELS);
    }

    if (config.DISCLAIMER_ENABLED) {
        tabs.push(MetaDataTab.DISCLAIMER);
    }

    tabs.push(MetaDataTab.IMAGE);

    return tabs;
}

/**
 * @name FileMetaDataTabView
 * @description
 * The dialog to add the metadata of a newly created file or edited file
 */
export const FileMetaDataTabView: React.VFC<FileMetaDataTabViewProps> = (props) => {
    const getApplicableTabs = (): string[] => {
        return getTabs().filter(tab =>
            (((MetaDataTab.MAIN === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_NAME, props.templatePropertySettings)
                        || isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, props.templatePropertySettings)
                        || ((isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)
                                && (isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, props.templatePropertySettings)))
                            || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                                && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_COMMENT, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, props.templatePropertySettings))))))
                || ((MetaDataTab.ADDITIONAL === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, props.templatePropertySettings)))
                    || props.isWatermarkApplicable)
                || ((MetaDataTab.PERMISSIONS === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, props.templatePropertySettings)
                        || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, props.templatePropertySettings))))
                || ((MetaDataTab.LOCALES === tab)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings))
                || ((MetaDataTab.LABELS === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_USER_LABELS, props.templatePropertySettings)))
                || ((MetaDataTab.DISCLAIMER === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DISCLAIMER, props.templatePropertySettings)))
                || ((MetaDataTab.IMAGE === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL, props.templatePropertySettings))))
        ) ?? [] as string[];
    }

    const [tabs] = useState(getApplicableTabs());
    const [activeTab, setActiveTab] = useState(getTab(MetaDataTab.MAIN, tabs));

    const handleTabClick = (tab: React.SetStateAction<string>) => {
        setActiveTab(tab);
    };

    return (
        (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
            || isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)) ?
            <TabNavigationWrapper id={generateIdentifier()} className='a-tab-navigation__wrapper'>
                <div id={generateIdentifier()} className='a-tab-navigation__gradients'>
                    <TabNavigation id={generateIdentifier()} className='a-tab-navigation' role='tablist'>
                        {tabs?.map((tab, index) => (
                            <li id={generateIdentifier(`'data-tab-item-'${index}`, '-')}
                                key={`'data-tab-item-'${index}`}
                                className='a-tab-navigation__item'
                                role='none'>
                                <TabButton id={generateIdentifier(`'data-tab-'${tab}`, '-')}
                                           type='button'
                                           className={`a-tab-navigation__tab ${activeTab === tab ? '-selected' : ''}`}
                                           data-frok-tab-identifier={`${index + 1}`}
                                           tabIndex={index}
                                           role='tab'
                                           aria-controls={`'data-tab-'${tab}`}
                                           onClick={() => handleTabClick(tab)}>
                                    <span id={generateIdentifier()} className='a-tab-navigation__tab-content'>
                                        <span id={generateIdentifier()}
                                              className='a-tab-navigation__label'>{t(tab)}</span>
                                    </span>
                                </TabButton>
                            </li>
                        ))}
                    </TabNavigation>
                    {(MetaDataTab.MAIN === activeTab) &&
                        <FileMetaDataTabMain templatePropertySettings={props.templatePropertySettings}
                                             item={props.item}
                                             parentLocales={props.parentLocales}
                                             language={props.language}
                                             defaultLocale={props.defaultLocale}
                                             labelsInitialState={props.labelsInitialState}
                                             versionInitialState={props.versionInitialState}
                                             eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.ADDITIONAL === activeTab) &&
                        <FileMetaDataTabAdditional templatePropertySettings={props.templatePropertySettings}
                                                   dateValidFrom={props.item.metadata.dateValidFrom}
                                                   dateValidTo={props.item.metadata.dateValidTo}
                                                   tags={props.item.metadata.tags}
                                                   isWatermarkApplicable={props.isWatermarkApplicable}
                                                   watermark={props.item.metadata.watermark}
                                                   eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.PERMISSIONS === activeTab) &&
                        <FileMetaDataTabPermissions templatePropertySettings={props.templatePropertySettings}
                                                    isPublic={props.item.isPublic ?? false}
                                                    ownerRoles={props.item.metadata.ownerRoles}
                                                    roles={props.item.metadata.roles}
                                                    parentOwnerRoles={props.parentOwnerRoles}
                                                    parentRoles={props.parentRoles}
                                                    rolesDataSource={props.rolesDataSource}
                                                    eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.LOCALES === activeTab) &&
                        <FileMetaDataTabLocales templatePropertySettings={props.templatePropertySettings}
                                                labels={props.item.labels}
                                                parentLocales={props.parentLocales}
                                                defaultLocale={props.defaultLocale}
                                                labelsInitialState={props.labelsInitialState}
                                                eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.LABELS === activeTab) &&
                        <FileMetaDataTabUserLabels templatePropertySettings={props.templatePropertySettings}
                                                   dataSource={props.labelData}
                                                   userLabels={props.item.metadata.userLabels}
                                                   eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.DISCLAIMER === activeTab) &&
                        <FileMetaDataTabDisclaimer templatePropertySettings={props.templatePropertySettings}
                                                   disclaimerData={props.disclaimerData}
                                                   disclaimer={props.item.metadata.disclaimer}
                                                   watermark={props.item.metadata.watermark}
                                                   eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.IMAGE === activeTab) &&
                        <FileMetaDataTabImage templatePropertySettings={props.templatePropertySettings}
                                              thumbnailFile={props.thumbnailFile}
                                              eventEmitter={props.eventEmitter}/>
                    }
                </div>
            </TabNavigationWrapper> :
            <TabNavigationWrapper id={generateIdentifier()} className='a-tab-navigation__wrapper'/>
    );
}